<template>
  <div class="itens-tree-editor">
    <!-- Level 0 items -->
    <div v-for="(item, i0) in localItens" :key="'l0-' + i0" class="nivel-0-wrapper mb-4">
      <v-card outlined class="nivel-card nivel-0-card">
        <v-card-text class="pb-1">
          <!-- Level 0 header -->
          <div class="d-flex align-center nivel-0-header">
            <v-icon small color="teal darken-1" class="mr-2">mdi-folder-outline</v-icon>
            <span
              class="nivel-label teal--text text--darken-2 font-weight-medium text-caption text-uppercase mr-2"
            >
              {{ labelNivel0 }}
            </span>
            <v-spacer />
            <v-tooltip v-if="!podeRemover(item)" bottom>
              <template #activator="{ on }">
                <v-btn icon small color="grey" v-on="on">
                  <v-icon small>mdi-information-outline</v-icon>
                </v-btn>
              </template>
              <span>Não é possível deletar {{ labelNivel0.toLowerCase() }} após criada</span>
            </v-tooltip>
            <v-btn v-else icon small color="error" @click="removerItem(localItens, i0)">
              <v-icon md>mdi-delete-outline</v-icon>
            </v-btn>
          </div>

          <ValidationProvider
            :name="`${labelNivel0} ${i0 + 1}`"
            :rules="{ required: true, max_chars: 100, no_special_chars: true }"
            v-slot="{ errors }"
          >
            <v-text-field
              v-model="item.descricao"
              :label="labelNivel0 + ' *'"
              outlined
              dense
              class="mt-4 mb-1"
              hide-details="auto"
              :error-messages="errors"
              counter="100"
              maxlength="100"
              @input="emitir"
            />
          </ValidationProvider>
          <v-divider />
          <v-spacer />

          <!-- Level 1 items (children of nivel 0) -->
          <div class="mt-2">
            <v-btn
              v-if="!item.children.length"
              text
              small
              class="mb-2 nivel-1-btn"
              @click="adicionarFilho(item.children)"
            >
              <v-icon small left>mdi-plus</v-icon>
              Adicionar {{ labelNivel1 }}
            </v-btn>
            <div
              v-for="(child1, i1) in item.children"
              :key="'l1-' + i0 + '-' + i1"
              class="nivel-1-wrapper mb-3"
            >
              <v-card outlined class="nivel-card nivel-1-card">
                <v-card-text class="pb-1">
                  <div class="d-flex align-center nivel-1-header">
                    <v-icon small color="blue-grey" class="mr-2">mdi-file-outline</v-icon>
                    <span
                      class="nivel-label nivel-1-label font-weight-medium text-caption text-uppercase mr-2"
                    >
                      {{ labelNivel1 }}
                    </span>
                    <v-spacer />
                    <v-tooltip v-if="!podeRemover(child1)" bottom>
                      <template #activator="{ on }">
                        <v-btn icon small color="grey" v-on="on">
                          <v-icon small>mdi-information-outline</v-icon>
                        </v-btn>
                      </template>
                      <span
                        >Não é possível deletar {{ labelNivel1.toLowerCase() }} após criado</span
                      >
                    </v-tooltip>
                    <v-btn v-else icon small color="error" @click="removerItem(item.children, i1)">
                      <v-icon md>mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>

                  <!-- Infantil: nivel 1 é folha (apelido + descricao) -->
                  <template v-if="isInfantil">
                    <v-row dense class="mt-1">
                      <v-col cols="3">
                        <ValidationProvider
                          :name="`Código ${i0 + 1}.${i1 + 1}`"
                          :rules="{ required: true, max_chars: 10, no_special_chars: true }"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            v-model="child1.apelido"
                            label="Código *"
                            outlined
                            dense
                            hide-details="auto"
                            :error-messages="errors"
                            counter="10"
                            maxlength="10"
                            @input="emitir"
                          />
                        </ValidationProvider>
                      </v-col>
                      <v-col cols="9">
                        <ValidationProvider
                          :name="`${labelNivel1} ${i0 + 1}.${i1 + 1}`"
                          :rules="{ required: true, max_chars: 500, no_special_chars: true }"
                          v-slot="{ errors }"
                        >
                          <v-textarea
                            v-model="child1.descricao"
                            :label="labelNivel1 + ' *'"
                            outlined
                            dense
                            rows="3"
                            auto-grow
                            hide-details="auto"
                            :error-messages="errors"
                            counter="500"
                            maxlength="500"
                            @input="emitir"
                          />
                        </ValidationProvider>
                      </v-col>
                    </v-row>
                  </template>

                  <!-- Fundamental: nivel 1 tem filhos (nivel 2) -->
                  <template v-else>
                    <ValidationProvider
                      :name="`${labelNivel1} ${i0 + 1}.${i1 + 1}`"
                      :rules="{ required: true, max_chars: 100, no_special_chars: true }"
                      v-slot="{ errors }"
                    >
                      <v-text-field
                        v-model="child1.descricao"
                        :label="labelNivel1 + ' *'"
                        outlined
                        dense
                        class="mt-4 mb-1"
                        hide-details="auto"
                        :error-messages="errors"
                        counter="100"
                        maxlength="100"
                        @input="emitir"
                      />
                    </ValidationProvider>

                    <!-- Level 2 items (children of nivel 1) -->
                    <div class="mt-2">
                      <div
                        v-for="(child2, i2) in child1.children"
                        :key="'l2-' + i0 + '-' + i1 + '-' + i2"
                        class="nivel-2-wrapper mb-2"
                      >
                        <v-card outlined class="nivel-card nivel-2-card">
                          <v-card-text class="py-2">
                            <div class="d-flex align-center mb-1">
                              <v-icon small color="amber darken-2" class="mr-1"
                                >mdi-star-outline</v-icon
                              >
                              <span
                                class="nivel-label amber--text text--darken-3 font-weight-medium text-caption text-uppercase"
                              >
                                {{ labelNivel2 }}
                              </span>
                              <v-spacer />
                              <v-tooltip v-if="!podeRemover(child2)" bottom>
                                <template #activator="{ on }">
                                  <v-btn icon small color="grey" v-on="on">
                                    <v-icon small>mdi-information-outline</v-icon>
                                  </v-btn>
                                </template>
                                <span
                                  >Não é possível deletar {{ labelNivel2.toLowerCase() }} após
                                  criada</span
                                >
                              </v-tooltip>
                              <v-btn
                                v-else
                                icon
                                small
                                color="error"
                                @click="removerItem(child1.children, i2)"
                              >
                                <v-icon md>mdi-delete-outline</v-icon>
                              </v-btn>
                            </div>
                            <v-row dense>
                              <v-col cols="3">
                                <ValidationProvider
                                  :name="`Código ${i0 + 1}.${i1 + 1}.${i2 + 1}`"
                                  :rules="{ required: true, max_chars: 10, no_special_chars: true }"
                                  v-slot="{ errors }"
                                >
                                  <v-text-field
                                    v-model="child2.apelido"
                                    label="Código *"
                                    outlined
                                    dense
                                    hide-details="auto"
                                    :error-messages="errors"
                                    counter="10"
                                    maxlength="10"
                                    @input="emitir"
                                  />
                                </ValidationProvider>
                              </v-col>
                              <v-col cols="9">
                                <ValidationProvider
                                  :name="`${labelNivel2} ${i0 + 1}.${i1 + 1}.${i2 + 1}`"
                                  :rules="{
                                    required: true,
                                    max_chars: 500,
                                    no_special_chars: true,
                                  }"
                                  v-slot="{ errors }"
                                >
                                  <v-textarea
                                    v-model="child2.descricao"
                                    :label="labelNivel2 + ' *'"
                                    outlined
                                    dense
                                    rows="3"
                                    auto-grow
                                    hide-details="auto"
                                    :error-messages="errors"
                                    counter="500"
                                    maxlength="500"
                                    @input="emitir"
                                  />
                                </ValidationProvider>
                              </v-col>
                            </v-row>
                            <v-btn
                              v-if="i2 === child1.children.length - 1"
                              text
                              small
                              color="amber darken-2"
                              class="mt-1 nivel-2-btn"
                              @click="adicionarFilho(child1.children)"
                            >
                              <v-icon small left>mdi-plus</v-icon>
                              Adicionar {{ labelNivel2 }}
                            </v-btn>
                          </v-card-text>
                        </v-card>
                      </div>
                      <v-btn
                        v-if="!child1.children.length"
                        text
                        small
                        color="amber darken-2"
                        class="mt-1 nivel-2-btn"
                        @click="adicionarFilho(child1.children)"
                      >
                        <v-icon small left>mdi-plus</v-icon>
                        Adicionar {{ labelNivel2 }}
                      </v-btn>
                    </div>
                  </template>
                  <v-btn
                    v-if="
                      i1 === item.children.length - 1 && (isInfantil || child1.children.length > 0)
                    "
                    text
                    small
                    class="mt-1 nivel-1-btn"
                    @click="adicionarFilho(item.children)"
                  >
                    <v-icon small left>mdi-plus</v-icon>
                    Adicionar {{ labelNivel1 }}
                  </v-btn>
                </v-card-text>
              </v-card>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Add root item button -->
    <v-btn outlined color="teal darken-1" class="mt-2" @click="adicionarRaiz">
      <v-icon left small>mdi-plus</v-icon>
      Adicionar {{ labelNivel0 }}
    </v-btn>

    <div v-if="localItens.length === 0" class="text-center grey--text py-6">
      <v-icon size="40" color="grey lighten-2">mdi-playlist-plus</v-icon>
      <div class="mt-2 text-body-2">
        Nenhum item cadastrado. Clique em "Adicionar {{ labelNivel0 }}" para começar.
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ItensTreeEditor",

  props: {
    value: { type: Array, default: () => [] },
    isInfantil: { type: Boolean, default: false },
    editing: { type: Boolean, default: false },
  },

  data() {
    return { localItens: [] };
  },

  computed: {
    labelNivel0() {
      return this.isInfantil ? "Campo de Experiência" : "Unidade Temática";
    },
    labelNivel1() {
      return this.isInfantil ? "Objetivo de Aprendizagem" : "Objeto de Conhecimento";
    },
    labelNivel2() {
      return "Habilidade";
    },
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        this.localItens = this.deepClone(val || []);
      },
    },
  },

  methods: {
    podeRemover(item) {
      return !this.editing || !!(item && item.isNovo);
    },

    novoItem(extra = {}) {
      return { descricao: "", apelido: null, ordem: 0, children: [], isNovo: true, ...extra };
    },

    adicionarRaiz() {
      this.localItens.push(this.novoItem({ ordem: this.localItens.length }));
      this.emitir();
    },

    adicionarFilho(lista) {
      lista.push(this.novoItem({ ordem: lista.length }));
      this.emitir();
    },

    adicionarObjeto(lista) {
      const objeto = this.novoItem({ ordem: lista.length });
      if (!this.isInfantil) {
        objeto.children.push(this.novoItem({ ordem: 0 }));
      }
      lista.push(objeto);
      this.emitir();
    },

    removerItem(lista, index) {
      lista.splice(index, 1);
      this.emitir();
    },

    emitir() {
      this.$emit("input", this.deepClone(this.localItens));
    },

    deepClone(obj) {
      return JSON.parse(JSON.stringify(obj));
    },
  },
};
</script>

<style scoped>
.itens-tree-editor {
  width: 100%;
}

.nivel-card {
  border-radius: 8px !important;
  transition: box-shadow 0.2s;
}

.nivel-0-card {
  border: 2px solid #00695c !important;
  background-color: #fafffe !important;
}

.nivel-1-card {
  border: 2px solid #3370b2 !important;
  background-color: #fafafa !important;
}

.nivel-2-card {
  border-left: 3px solid #f9a825 !important;
  background-color: #fffdf0 !important;
}
.nivel-2-btn {
  border: 1px solid #f9a825 !important;
  color: #f9a825 !important;
}

.nivel-0-header {
  background-color: #e0f2f0;
  margin: -16px -16px 8px -16px;
  padding: 8px 16px;
  border-radius: 6px 6px 0 0;
}

.nivel-1-header {
  background-color: #deeaf6;
  margin: -16px -16px 8px -16px;
  padding: 8px 16px;
  border-radius: 6px 6px 0 0;
}

.nivel-1-label {
  color: #3370b2 !important;
}

.nivel-1-btn {
  border: 1px solid #3370b2 !important;
  color: #3370b2 !important;
}

.nivel-0-header {
  background-color: #e0f2f0;
  margin: -16px -16px 8px -16px;
  padding: 8px 16px;
  border-radius: 6px 6px 0 0;
}

.nivel-1-header {
  background-color: #deeaf6;
  margin: -16px -16px 8px -16px;
  padding: 8px 16px;
  border-radius: 6px 6px 0 0;
}

.nivel-1-label {
  color: #3370b2 !important;
}

.nivel-label {
  letter-spacing: 0.06em;
  font-size: 10px !important;
}

.nivel-1-container {
  border-left: 2px dashed #e0e0e0;
}
.nivel-2-container {
  border-left: 2px dashed #ffe082;
}
</style>
